import type {AreaDispatcher} from '@gaia/hooks/useAreas';
import type {TrackDispatcher} from '@gaia/hooks/useTracks';
import type {WaypointDispatcher} from '@gaia/hooks/useWaypoints';
import {registerSavedFolder} from '@gaia/utils/gaia/saveFolder';
import findKey from 'lodash/findKey';
import keyBy from 'lodash/keyBy';
import pickBy from 'lodash/pickBy';

import {createFolder} from './create';

// eslint-disable-next-line max-params
export default function bulkArchive(
    action: BulkArchiveFolderAction,
    setFolders: React.Dispatch<React.SetStateAction<{[localId: string]: Folder}>>,
    areaDispatcher: AreaDispatcher,
    trackDispatcher: TrackDispatcher,
    waypointDispatcher: WaypointDispatcher
) {
    if (!action.ids.length) return [];
    const {archived, children} = action;
    let allFolderIds = action.ids;

    if (children) {
        allFolderIds = allFolderIds.concat(children.folders || []);
        areaDispatcher({
            type: 'bulkArchive',
            archived,
            ids: children.areas || [],
        });
        trackDispatcher({
            type: 'bulkArchive',
            archived,
            ids: children.tracks || [],
        });
        waypointDispatcher({
            type: 'bulkArchive',
            archived,
            ids: children.waypoints || [],
        });
    }

    setFolders((latestFolders) => {
        let archivedFolders = allFolderIds.map((id) => archiveFolder(id, archived, latestFolders));

        if (action.archived === false) {
            archivedFolders = archivedFolders.filter((folder) => !folder.bulkArchived);
        }

        // calling registerSavedFolder directly is an escape hatch to prevent the
        // save effect from picking up this change
        archivedFolders.forEach((folder) => registerSavedFolder(folder));

        // If we are unarchiving, remove the partial local instance
        const existingFolders =
            action.archived === false
                ? pickBy(latestFolders, (folder) => !folder.bulkArchived)
                : latestFolders;

        return {
            ...existingFolders,
            ...keyBy(archivedFolders, (folder) => folder.localId),
        };
    });

    return action.ids;
}

export function archiveFolder(id: string, archived: boolean, folders: {[localId: string]: Folder}) {
    // If the folder has been created in the current session, its local and remote IDs will differ.
    // Archive could be called on either, so we need to look the folder up by both IDs.
    const localId = findKey(folders, (f) => f.id === id) || id;
    if (folders[localId]) {
        return {
            ...folders[localId],
            archived,
        };
    } else {
        // Create an empty folder to make sure we don't show a folder
        // that has been deleted without being taken local
        const newFolder = createFolder();
        return {
            ...newFolder,
            localId: id,
            id,
            archived: true,
            bulkArchived: true,
        };
    }
}
